// Maintains an estimated offset between server time and local monotonic clock.
// Offset is defined so that: server_ts ≈ performance.now() + offset.
// Initial value comes from state_snapshot/hello exchange; refined every 60s via ping/pong.
// A drift > SNAP_THRESHOLD_MS causes a hard snap (emits onSnap callback so scenes can reconcile).

export interface ClockSyncOpts {
  snapThresholdMs?: number;
  resyncIntervalMs?: number;
  onSnap?: (prevOffset: number, newOffset: number) => void;
}

export class ClockSync {
  private offsetMs = 0;
  private samples: number[] = [];
  private readonly snapThresholdMs: number;
  private readonly resyncIntervalMs: number;
  private readonly onSnap?: (prev: number, next: number) => void;
  private resyncTimer: ReturnType<typeof setInterval> | null = null;
  private initialized = false;

  constructor(opts: ClockSyncOpts = {}) {
    this.snapThresholdMs = opts.snapThresholdMs ?? 200;
    this.resyncIntervalMs = opts.resyncIntervalMs ?? 60_000;
    if (opts.onSnap !== undefined) this.onSnap = opts.onSnap;
  }

  /** server_ts corresponding to local performance.now(). */
  toServerTs(localNowMs: number = performance.now()): number {
    return localNowMs + this.offsetMs;
  }

  /** Local performance.now() corresponding to a server timestamp. */
  toLocalMs(serverTs: number): number {
    return serverTs - this.offsetMs;
  }

  /** How many ms until the server reaches server_ts, measured against the local monotonic clock. */
  msUntil(serverTs: number): number {
    return this.toLocalMs(serverTs) - performance.now();
  }

  get offset(): number {
    return this.offsetMs;
  }

  get isInitialized(): boolean {
    return this.initialized;
  }

  /** Called on state_snapshot arrival — single-sample bootstrap. */
  bootstrap(serverTs: number, localReceivedMs: number = performance.now()): void {
    this.applyOffset(serverTs - localReceivedMs);
    this.initialized = true;
  }

  /** Classic NTP pong handling. t1 = server receive, t2 = server send. */
  applyPong(t0LocalMs: number, t1ServerMs: number, t2ServerMs: number, t3LocalMs: number): void {
    // Offset estimate (median of the two directions) and round-trip time.
    // offset = ((t1 - t0) + (t2 - t3)) / 2
    const sample = (t1ServerMs - t0LocalMs + (t2ServerMs - t3LocalMs)) / 2;
    this.samples.push(sample);
    if (this.samples.length > 7) this.samples.shift();
    // Use median of collected samples to be robust to outliers.
    const median = medianOf(this.samples);
    this.applyOffset(median);
    this.initialized = true;
  }

  private applyOffset(next: number): void {
    const prev = this.offsetMs;
    const delta = Math.abs(next - prev);
    this.offsetMs = next;
    if (this.initialized && delta > this.snapThresholdMs && this.onSnap) {
      this.onSnap(prev, next);
    }
  }

  startResyncLoop(sendPing: () => void): void {
    this.stopResyncLoop();
    this.resyncTimer = setInterval(sendPing, this.resyncIntervalMs);
  }

  stopResyncLoop(): void {
    if (this.resyncTimer !== null) {
      clearInterval(this.resyncTimer);
      this.resyncTimer = null;
    }
  }
}

function medianOf(xs: number[]): number {
  if (xs.length === 0) return 0;
  const sorted = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2 : (sorted[mid] ?? 0);
}
